import { Timestamp } from "firebase/firestore";
import {
  Candidate,
  Candidate_FB,
  CandidateStatus,
  CampaignDoc,
  CANDIDATE_STATUS_LIST,
} from "../types/recruiting";
import { Chip, Typography } from "@mui/material";
import { calculateAge, fbTimestampToDateTime, monthsBetween } from "./utils";
import { BASE_FUNCTIONS_URL } from "../types/firebase";
import {
  DISQUALIFY_NUM_VALUE,
  Question,
  QuestionAnswersDict,
} from "../types/surveys";
import { loadCandidates } from "../data-functions/recruiting_api";
import { AppUser } from "../types/users";
import {
  ADVANCED_FORM_QUESTIONS,
  RUSSIAN_QUESTIONS,
} from "../pages/recruiting/interview-questions";

export const splitDisplayName = (displayName: string) => {
  const trimmed = displayName.trim();
  if (!trimmed) {
    return { firstName: "", lastName: "" };
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }

  const [firstName, ...rest] = parts;
  return { firstName, lastName: rest.join(" ") };
};

export const getCandidateStatusText = (status: CandidateStatus | undefined) => {
  switch (status) {
    case "verified":
      return "Verified";

    case "language_test":
      return "Language Test";

    case "language_test_passed":
      return "Test - passed";

    case "language_test_failed":
      return "Test - failed";

    case "invited":
      return "Invited";

    case "approved":
      return "Approved";

    case "applicant":
      return "Sent for Visa";

    case "disqualified":
      return "Disqualified";

    case "rejected":
      return "Rejected";

    case "DnI":
      return "Rejected";

    case "interviewed":
      return "Interviewed";

    default:
      return (
        CANDIDATE_STATUS_LIST.find((s) => s.key === status)?.label || "New"
      );
  }
};

export const getCandidateStatusLabel = (
  status: CandidateStatus | undefined
) => {
  return (
    <Typography paddingRight={"3px"}>
      {getCandidateStatusText(status)}
    </Typography>
  );
};

export const getCandidateStatusChip = (status: CandidateStatus | undefined) => {
  switch (status) {
    case "verified":
      return (
        <Chip
          label={getCandidateStatusText(status)}
          color="primary"
          sx={{
            backgroundColor: "rgba(25, 118, 210, 0.3)",
            color: "black",
            "& .MuiChip-label": {
              opacity: 1,
            },
          }}
        />
      );
    case "language_test":
      return (
        <Chip
          label={getCandidateStatusText(status)}
          color="primary"
          sx={{
            backgroundColor: "rgba(25, 118, 210, 0.6)",
            color: "black",
            "& .MuiChip-label": {
              opacity: 1,
            },
          }}
        />
      );
    case "language_test_passed":
      return <Chip label={getCandidateStatusText(status)} color="success" />;
    case "language_test_failed":
      return <Chip label={getCandidateStatusText(status)} color="error" />;
    case "invited":
      return <Chip label={getCandidateStatusText(status)} color="primary" />;
    case "interviewed":
      return <Chip label={getCandidateStatusText(status)} color="primary" />;
    case "approved":
      return <Chip label={getCandidateStatusText(status)} color="success" />;

    case "applicant":
      return <Chip label={getCandidateStatusText(status)} color="success" />;

    case "disqualified":
      return (
        <Chip
          label={getCandidateStatusText(status)}
          color="error"
          variant="outlined"
        />
      );

    case "rejected":
      return <Chip label={getCandidateStatusText(status)} color="error" />;

    case "DnI":
      return <Chip label={getCandidateStatusText(status)} color="error" />;

    default:
      return (
        <Chip
          label={getCandidateStatusText(status)}
          color="primary"
          variant="outlined"
        />
      );
  }
};

export const setCandidateToFB = (candidate: Candidate): Candidate_FB => {
  const cand: Candidate_FB = {
    ...candidate,
    verification: candidate.verification && {
      verificationCode: candidate.verification.verificationCode,
      expiryDate: Timestamp.fromDate(
        new Date(candidate.verification.expiryDate)
      ),
    },
  };

  if (cand.verification !== undefined) {
    if (cand.verification && candidate.verification?.confirmationCode) {
      cand.verification.confirmationCode =
        candidate.verification.confirmationCode;
    }

    if (
      cand.verification &&
      candidate.verification &&
      candidate.verification.verifiedOn
    ) {
      cand.verification.verifiedOn = Timestamp.fromDate(
        new Date(candidate.verification.verifiedOn)
      );
    }
  } else {
    delete cand.verification;
  }

  return cand;
};

export const getCandidateFromFB = (candidate: Candidate_FB): Candidate => {
  const cand: Candidate = {
    ...candidate,
    verification: candidate.verification && {
      verificationCode: candidate.verification.verificationCode,
      expiryDate: fbTimestampToDateTime(candidate.verification.expiryDate),
    },
  };

  if (cand.verification && candidate.verification?.confirmationCode) {
    cand.verification.confirmationCode =
      candidate.verification.confirmationCode;
  }

  if (
    cand.verification &&
    candidate.verification &&
    candidate.verification.verifiedOn
  ) {
    cand.verification.verifiedOn = fbTimestampToDateTime(
      candidate.verification.verifiedOn
    );
  }
  return cand;
};

export const sendCandidateEmail = async (
  candidateIds: string[],
  templateId: string
) => {
  const apiKey = process.env.REACT_APP_FUNCTIONS_API_KEY || "";
  const response = await fetch(BASE_FUNCTIONS_URL + "/public/mailCandidates", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": apiKey,
    },
    body: JSON.stringify({
      candidates: candidateIds,
      template: templateId,
    }),
  });

  return response;
};

export const calculateSurveyScore = (
  surveyAnswers: QuestionAnswersDict,
  questions: Question[],
  campaignScores?: Record<string, Question["score"]>
) => {
  let score = 0;
  for (const question of questions) {
    const effectiveScore =
      (campaignScores && campaignScores[question.id]) || question.score;

    if (effectiveScore) {
      const answer = surveyAnswers[question.id];

      if (effectiveScore["_any_answer_"] !== undefined) {
        if (answer) {
          const anyVal = effectiveScore["_any_answer_"];
          if (anyVal === "disqualify") {
            return DISQUALIFY_NUM_VALUE;
          }
          if (typeof anyVal === "number") {
            score += anyVal;
          }
        }
      } else if (effectiveScore["_no_answer_"] !== undefined) {
        if (!answer) {
          const noVal = effectiveScore["_no_answer_"];
          if (noVal === "disqualify") {
            return DISQUALIFY_NUM_VALUE;
          }
          if (typeof noVal === "number") {
            score += noVal;
          }
        }
      } else {
        if (typeof answer === "string") {
          const scVal = effectiveScore[answer as string];
          if (scVal === "disqualify") {
            return DISQUALIFY_NUM_VALUE;
          }
          if (typeof scVal === "number") {
            score += scVal;
          }
        } else if (answer && typeof answer === "object" && question.options) {
          // multi select question
          for (const oAnswer of Object.keys(answer)) {
            if (answer[oAnswer] === "true") {
              const scVal = effectiveScore[oAnswer];
              if (scVal === "disqualify") {
                return DISQUALIFY_NUM_VALUE;
              }
              if (typeof scVal === "number") {
                score += scVal;
              }
            }
          }
        }
      }
    }
  }
  return score;
};

export const getTravelCompanionLabel = (travelCompanion?: string) => {
  switch (travelCompanion) {
    case "spouse":
      return "With spouse";
    case "relative_friend":
      return "With relative / friend";
    default:
      return "Alone";
  }
};

export const getCompanionRelationLabel = (relation?: string) => {
  switch (relation) {
    case "sibling":
      return "Sibling";
    case "parent":
      return "Parent";
    case "friend":
      return "Friend";
    case "other_relative":
      return "Other relative";
    default:
      return "-";
  }
};

export const calcScore = async (
  appUser: AppUser | undefined | null,
  candidate: Candidate,
  currentCampaign: CampaignDoc,
  duplicateCache?: Map<string, { lastVerifiedOn?: string; campaignId?: string }>
) => {
  const campaign = currentCampaign.campaign;
  if (!campaign) return 0;

  if (campaign.disqualifyIfNoPassport && candidate.has_passport === false) {
    return DISQUALIFY_NUM_VALUE;
  }

  // Disqualify if candidate is traveling with spouse but unwilling to travel alone
  // if (
  //    candidate.travelCompanion === "spouse" &&
  //    candidate.travelIfSpouseUnsuccessful === false) {
  //   return DISQUALIFY_NUM_VALUE;
  // }

  if (campaign.disqualifyIfAgeInvalid && campaign.minAge && campaign.maxAge) {
    const age = calculateAge(candidate.birth_date);
    if (age < campaign.minAge || age > campaign.maxAge || age === 0) {
      return DISQUALIFY_NUM_VALUE;
    }
  }

  if (campaign.disqualifyIfAlreadyRegistered && campaign.cooldownMonths) {
    return DISQUALIFY_NUM_VALUE;
  }

  // Disqualify if already registered in another campaign within cooldown window
  if (
    campaign.disqualifyIfAlreadyRegistered &&
    campaign.cooldownMonths &&
    duplicateCache
  ) {
    const campaignId = candidate.campaignId || currentCampaign.id;
    const cacheKey = `${(candidate.email || "").toLowerCase()}::${campaignId}`;

    let cached = duplicateCache.get(cacheKey);
    if (!cached) {
      const sameEmailCandidates = await loadCandidates(
        appUser,
        "all_campaigns",
        {
          email: { value: candidate.email, operator: "==" },
        }
      );

      let lastVerifiedOn: string | undefined = undefined;
      let lastVerifiedCampaignId: string | undefined = undefined;

      for (const [, cand] of Object.entries(sameEmailCandidates)) {
        if (!cand.campaignId || cand.campaignId === campaignId) continue; // only other campaigns
        const v = cand.verification?.verifiedOn;
        if (v) {
          if (!lastVerifiedOn || v > lastVerifiedOn) {
            lastVerifiedOn = v;
            lastVerifiedCampaignId = cand.campaignId;
          }
        }
      }

      cached = { lastVerifiedOn, campaignId: lastVerifiedCampaignId };
      if (duplicateCache) duplicateCache.set(cacheKey, cached);
    }

    if (cached.lastVerifiedOn) {
      const monthsSince = monthsBetween(
        cached.lastVerifiedOn,
        new Date().toISOString()
      );
      if (monthsSince < campaign.cooldownMonths) {
        return DISQUALIFY_NUM_VALUE;
      }
    }
  }

  if (candidate.surveyAnswers && campaign) {
    let score = calculateSurveyScore(
      candidate.surveyAnswers,
      ADVANCED_FORM_QUESTIONS,
      campaign.surveyQuestionScores
    );

    if (campaign?.language === "ru" && score > DISQUALIFY_NUM_VALUE) {
      const sc = calculateSurveyScore(
        candidate.surveyAnswers,
        RUSSIAN_QUESTIONS,
        campaign.surveyQuestionScores
      );

      if (sc > DISQUALIFY_NUM_VALUE) {
        score += sc;
      } else {
        score = DISQUALIFY_NUM_VALUE;
      }
    }
    return score;
  } else {
    return 0;
  }
};
